/** @jsxImportSource @emotion/react */
import React from "react";
import { Modal } from "../../../components/Modal";
import * as authSelectors from "../../auth/authSelectors";
import useUpdateFormErrors from "../../common/hook-form/useUpdateFormErrors";
import {
  useCreateUserVoucher,
  useGetUserVoucher,
  useGetVouchers,
} from "../../../service/api";
import { useSelector } from "react-redux";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import { FormProvider, useForm } from "react-hook-form";
import { SelectInput } from "../../../components/forms/inputs/SelectInput";
import { TextInput } from "../../../components/forms/inputs/TextInput";
import { TextAreaInput } from "../../../components/forms/inputs/TextAreaInput";
import { SpinnerBlock } from "../../common/Spinner";
import { ButtonAction } from "../../../components/NewButtons";
import { ActionSpinner } from "../../../components/ActionSpinner";
import { css } from "@emotion/react";
import { getErrorMessage, useHandleFetchError } from "../../../error-handler";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  voucherId?: string;
};

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const AddVoucherToUserModal = ({
  isOpen,
  onClose,
  userId,
  voucherId,
}: Props) => {
  const userVoucherId = voucherId;
  const isEdit = userVoucherId !== "__new";
  const formMethods = useForm();

  const { handleSubmit, setError, reset } = formMethods;

  const organization =
    useSelector(authSelectors.selectedOrganizationKeyName) || "";

  // Only runs if isEdit is true - it is never true. Keeping it here for now.
  const {
    data: userVoucher,
    isLoading: isLoadingUserVoucher,
    error: userVoucherError,
  } = useGetUserVoucher(organization, isEdit, userVoucherId);

  const {
    data: voucherTypes,
    isLoading: isLoadingVoucherTypes,
    error: voucherTypesError,
  } = useGetVouchers(organization);

  const {
    mutateAsync: createUserVoucher,
    error: createUserVoucherError,
    isLoading: isLoadingCreation,
  } = useCreateUserVoucher(userId);

  useUpdateFormErrors(createUserVoucherError, setError);

  React.useEffect(() => {
    if (isEdit && userVoucher) {
      reset({
        notes: userVoucher.notes,
        voucherTypeId: userVoucher.voucherTypeId,
        expiresAt: moment(userVoucher.expiresAt).format("YYYY-MM-DD"),
      });
    }
  }, [isEdit, userVoucher, reset]);

  const onSubmit = async (data: any) => {
    try {
      await createUserVoucher({
        data: {
          ...data,
          userId,
          expiresAt: data.expiresAt
            ? moment(data.expiresAt).format("YYYY-MM-DDTHH:mm:ssZ")
            : null,
        },
        organizationKeyName: organization,
      });
      onClose();
      reset();
      toastr.success("Success", "Voucher added to user successfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const voucherTypeOptions = [
    { label: "", value: "" },
    ...(voucherTypes || [])
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((x) => ({
        label: x.name,
        value: x.id,
      })),
  ];

  const isLoading = isLoadingUserVoucher || isLoadingVoucherTypes;

  useHandleFetchError(userVoucherError);
  useHandleFetchError(voucherTypesError);

  return (
    <Modal title="Add voucher" isOpen={isOpen} onClose={onClose}>
      <FormProvider {...formMethods}>
        <form css={bodyStyle}>
          {isEdit && isLoading ? (
            <SpinnerBlock />
          ) : (
            <>
              <TextInput label="Expires at" name="expiresAt" type="date" />
              <SelectInput
                label="Voucher Type"
                name="voucherTypeId"
                options={voucherTypeOptions}
                placeholder="Start typing to search..."
              />
              <TextAreaInput label="Notes" name="notes" />
              <ButtonAction
                width="100%"
                onClick={handleSubmit(onSubmit)}
                type="button"
                disabled={isLoadingCreation}
              >
                {isLoadingCreation ? <ActionSpinner /> : "Save"}
              </ButtonAction>
            </>
          )}
        </form>
      </FormProvider>
    </Modal>
  );
};
